import { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Input,
  InputNumber,
  Modal,
  Radio,
  Space,
  Spin,
  Tag,
  Typography,
  message,
} from "antd";
import { useTokenInfo } from "../../hooks/useTokenInfo";
import {
  getQuestionnaireSections,
  getSectionQuestions,
  getQuestionChoices,
  getMyQuestionnaireAnswers,
  getQuestionnaireQuestionAnswers,
  createQuestionnaireAnswer,
  addQuestionnaireQuestionAnswer,
  updateQuestionnaireQuestionAnswer,
  completeQuestionnaireAnswer,
} from "../../repository/data.repository";
import { getProblemMessage } from "../../utils/http";

export interface QuestionnaireAnswerModalProps {
  readonly open: boolean;
  readonly questionnaireUid?: string | null;
  readonly questionnaireName?: string | null;
  readonly onClose: () => void;
}

interface LoadedChoice {
  uid: string;
  description: string;
}
interface LoadedQuestion {
  uid: string;
  description: string;
  questionType: "FreeText" | "SingleChoice" | "MultipleChoice" | "Numeric";
  minValue?: number;
  maxValue?: number;
  unit?: string;
  isRequired: boolean;
  choices: LoadedChoice[];
}
interface LoadedSection {
  uid: string;
  name: string;
  description: string;
  questions: LoadedQuestion[];
}

interface AnswerEntry {
  questionAnswerUid?: string;
  rowVersion?: string;
  freeText?: string;
  numeric?: number | null;
  selectedChoices: string[];
}

// Draft-first questionnaire answering for a company manager: load-or-create a draft answer,
// retain each question answer as it changes (real time), and let the manager "Complete" now or later.
function QuestionnaireAnswerModal(props: QuestionnaireAnswerModalProps) {
  const { userId, claims } = useTokenInfo();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sections, setSections] = useState<LoadedSection[]>([]);
  const [answerUid, setAnswerUid] = useState<string | null>(null);
  const [answerRowVersion, setAnswerRowVersion] = useState<string | null>(null);
  const [completed, setCompleted] = useState(false);
  const [answers, setAnswers] = useState<Record<string, AnswerEntry>>({});
  const [savingCount, setSavingCount] = useState(0);
  const [completing, setCompleting] = useState(false);
  // Flips true after a Complete attempt with unanswered required questions — gates the inline validation.
  const [attemptedComplete, setAttemptedComplete] = useState(false);

  // Per-question write serializer. React state is async, so we hold the AUTHORITATIVE
  // questionAnswerUid + rowVersion per question in a ref and thread it write-to-write. A write queue
  // (one in flight per question, coalescing to the latest value) prevents rapid choice toggles from
  // firing overlapping PATCHes with a stale rowVersion → 409.
  const writeMetaRef = useRef<Record<string, { questionAnswerUid?: string; rowVersion?: string }>>({});
  const writeQueueRef = useRef<
    Record<
      string,
      {
        inFlight: boolean;
        pending: { freeText?: string; numeric?: number | null; selectedChoices: string[] } | null;
      }
    >
  >({});

  const load = useCallback(async () => {
    const questionnaireUid = props.questionnaireUid;
    if (!questionnaireUid || !userId) {
      setError("You must be signed in to answer this questionnaire.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // 1) Questionnaire content: sections → questions → choices.
      const sectionsData = await getQuestionnaireSections(questionnaireUid);
      const loadedSections: LoadedSection[] = await Promise.all(
        sectionsData.data.map(async (s) => {
          const questionsData = await getSectionQuestions(s.uid);
          const questions: LoadedQuestion[] = await Promise.all(
            questionsData.data.map(async (q) => {
              const choices =
                q.questionType === "FreeText" || q.questionType === "Numeric"
                  ? { data: [] as any[] }
                  : await getQuestionChoices(q.uid);
              return {
                uid: q.uid,
                description: q.description,
                questionType: q.questionType,
                minValue: q.minValue,
                maxValue: q.maxValue,
                unit: q.unit,
                // "Must answer": marked required, or assessed — it feeds a banded assessment score, and the server
                // refuses completion while one is unanswered, so the form flags it the same way. Data-collection
                // questions (count KPIs) may be left empty: nothing ticked means "nothing to report".
                isRequired: q.isRequired || !!q.isAssessed,
                choices: choices.data.map((c) => ({ uid: c.uid, description: c.description })),
              };
            }),
          );
          return { uid: s.uid, name: s.name, description: s.description, questions };
        }),
      );
      setSections(loadedSections);

      // 2) Load-or-create my draft answer for this questionnaire.
      let aUid: string;
      let aRowVersion: string;
      let aCompleted = false;
      const mine = await getMyQuestionnaireAnswers(userId, questionnaireUid);
      const existing = mine.data[0];
      if (existing) {
        aUid = existing.uid;
        aRowVersion = existing.rowVersion;
        aCompleted = existing.completed;
      } else {
        const created = await createQuestionnaireAnswer({
          userUid: userId,
          questionnaireUid: questionnaireUid,
          companyUid: claims?.usrCmpUid ?? null,
        });
        aUid = created.uid;
        aRowVersion = created.rowVersion;
      }
      setAnswerUid(aUid);
      setAnswerRowVersion(aRowVersion);
      setCompleted(aCompleted);

      // 3) Hydrate existing question answers into the form state + the authoritative write-meta ref.
      const existingAnswers: Record<string, AnswerEntry> = {};
      writeMetaRef.current = {};
      writeQueueRef.current = {};
      if (existing) {
        const qa = await getQuestionnaireQuestionAnswers(aUid);
        for (const a of qa.data) {
          existingAnswers[a.questionUid] = {
            questionAnswerUid: a.uid,
            rowVersion: a.rowVersion,
            freeText: a.freeTextAnswer ?? undefined,
            numeric: a.numericAnswer ?? undefined,
            selectedChoices: a.selectedChoices ?? [],
          };
          writeMetaRef.current[a.questionUid] = { questionAnswerUid: a.uid, rowVersion: a.rowVersion };
        }
      }
      setAnswers(existingAnswers);
    } catch (e) {
      setError("Could not load the questionnaire. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [props.questionnaireUid, userId, claims]);

  useEffect(() => {
    if (props.open) {
      load();
    } else {
      // reset on close
      setSections([]);
      setAnswers({});
      setAnswerUid(null);
      setAnswerRowVersion(null);
      setCompleted(false);
      setError(null);
      setAttemptedComplete(false);
      writeMetaRef.current = {};
      writeQueueRef.current = {};
    }
  }, [props.open, load]);

  // Drain the per-question write queue: one write in flight at a time, always using the latest pending
  // value and the authoritative rowVersion threaded from the previous write.
  const drain = useCallback(
    async (questionUid: string) => {
      const q = writeQueueRef.current[questionUid];
      if (!q || q.inFlight || !answerUid) return;
      q.inFlight = true;
      setSavingCount((c) => c + 1);
      try {
        while (q.pending) {
          const value = q.pending;
          q.pending = null;
          const meta = writeMetaRef.current[questionUid] ?? {};
          if (!meta.questionAnswerUid) {
            const created = await addQuestionnaireQuestionAnswer({
              questionnaireAnswerUid: answerUid,
              questionUid,
              freeTextAnswer: value.freeText ?? null,
              numericAnswer: value.numeric ?? null,
              selectedChoices: value.selectedChoices,
            });
            writeMetaRef.current[questionUid] = {
              questionAnswerUid: created.uid,
              rowVersion: created.rowVersion,
            };
          } else {
            const updated = await updateQuestionnaireQuestionAnswer({
              uid: meta.questionAnswerUid,
              rowVersion: meta.rowVersion ?? "",
              freeTextAnswer: value.freeText ?? null,
              numericAnswer: value.numeric ?? null,
              selectedChoices: value.selectedChoices,
            });
            writeMetaRef.current[questionUid] = {
              questionAnswerUid: meta.questionAnswerUid,
              rowVersion: updated?.rowVersion ?? meta.rowVersion,
            };
          }
          const m = writeMetaRef.current[questionUid];
          setAnswers((prev) => ({
            ...prev,
            [questionUid]: {
              ...value,
              questionAnswerUid: m.questionAnswerUid,
              rowVersion: m.rowVersion,
            },
          }));
        }
      } catch (err) {
        message.error(
          getProblemMessage(err) ||
            "Couldn't save that answer — please retry.",
        );
      } finally {
        q.inFlight = false;
        setSavingCount((c) => c - 1);
      }
    },
    [answerUid],
  );

  // Queue the latest value for a question and kick the drain (coalesces rapid edits to that question).
  const queueWrite = useCallback(
    (
      questionUid: string,
      value: { freeText?: string; numeric?: number | null; selectedChoices: string[] },
    ) => {
      const q = (writeQueueRef.current[questionUid] ??= { inFlight: false, pending: null });
      q.pending = value;
      void drain(questionUid);
    },
    [drain],
  );

  function onChoiceChange(question: LoadedQuestion, selected: string[]): void {
    const current = answers[question.uid] ?? { selectedChoices: [] };
    const next: AnswerEntry = { ...current, selectedChoices: selected };
    setAnswers((prev) => ({ ...prev, [question.uid]: next }));
    queueWrite(question.uid, { freeText: next.freeText, numeric: next.numeric, selectedChoices: selected });
  }

  function onFreeTextChange(question: LoadedQuestion, value: string): void {
    const current = answers[question.uid] ?? { selectedChoices: [] };
    setAnswers((prev) => ({ ...prev, [question.uid]: { ...current, freeText: value } }));
  }

  function onFreeTextBlur(question: LoadedQuestion): void {
    const current = answers[question.uid];
    if (current)
      queueWrite(question.uid, {
        freeText: current.freeText,
        numeric: current.numeric,
        selectedChoices: current.selectedChoices ?? [],
      });
  }

  function onNumericChange(question: LoadedQuestion, value: number | null): void {
    const current = answers[question.uid] ?? { selectedChoices: [] };
    const next: AnswerEntry = { ...current, numeric: value };
    setAnswers((prev) => ({ ...prev, [question.uid]: next }));
    queueWrite(question.uid, {
      freeText: next.freeText,
      numeric: value,
      selectedChoices: next.selectedChoices ?? [],
    });
  }

  function isAnswered(question: LoadedQuestion): boolean {
    const a = answers[question.uid];
    if (!a) return false;
    if (question.questionType === "FreeText") return !!a.freeText?.trim();
    if (question.questionType === "Numeric") return a.numeric != null;
    return (a.selectedChoices?.length ?? 0) > 0;
  }

  // Once explicitly completed, the answer is locked: open for review, not editing.
  const readOnly = completed;

  const requiredMissing = sections
    .flatMap((s) => s.questions)
    .filter((q) => q.isRequired && !isAnswered(q));

  async function onComplete(): Promise<void> {
    if (!answerUid || !answerRowVersion) return;
    if (requiredMissing.length) {
      setAttemptedComplete(true);
      message.warning(`Please answer all required questions (${requiredMissing.length} left).`);
      return;
    }
    setCompleting(true);
    try {
      await completeQuestionnaireAnswer({
        uid: answerUid,
        rowVersion: answerRowVersion,
        completed: true,
      });
      setCompleted(true);
      message.success("Questionnaire completed.");
      props.onClose();
    } catch (err) {
      message.error(
        getProblemMessage(err) ||
          "Couldn't complete the questionnaire — please retry.",
      );
    } finally {
      setCompleting(false);
    }
  }

  return (
    <Modal
      open={props.open}
      title={
        <Space>
          <span>{props.questionnaireName ?? "Questionnaire"}</span>
          {completed ? (
            <Tag color="green">Completed</Tag>
          ) : (
            <Tag color="blue">Draft</Tag>
          )}
          {savingCount > 0 && <Typography.Text type="secondary">Saving…</Typography.Text>}
        </Space>
      }
      width={760}
      onCancel={props.onClose}
      maskClosable={false}
      footer={
        <Space>
          <Button onClick={props.onClose}>Close</Button>
          {!readOnly && (
            <Space>
              {attemptedComplete && requiredMissing.length > 0 && (
                <Typography.Text type="danger">
                  {requiredMissing.length} required left
                </Typography.Text>
              )}
              <Button
                type="primary"
                loading={completing}
                disabled={loading || !answerUid}
                onClick={onComplete}
              >
                Complete
              </Button>
            </Space>
          )}
        </Space>
      }
    >
      {loading ? (
        <div className="flex justify-center p-8">
          <Spin />
        </div>
      ) : error ? (
        <Alert type="error" message={error} />
      ) : (
        <div className="flex max-h-[60vh] flex-col gap-6 overflow-y-auto p-1">
          {readOnly ? (
            <Alert
              type="success"
              showIcon
              message="This questionnaire is completed and locked. You can review your answers but not change them."
            />
          ) : (
            <Typography.Text type="secondary">
              Your answers are saved as you go — you can close and finish later, then mark it
              Complete when you're done.
            </Typography.Text>
          )}
          {sections.map((section) => (
            <div key={section.uid} className="flex flex-col gap-3">
              <Typography.Title level={5} className="!mb-0">
                {section.name}
              </Typography.Title>
              {section.description && (
                <Typography.Text type="secondary">{section.description}</Typography.Text>
              )}
              {section.questions.map((q) => {
                const a = answers[q.uid];
                // Show the inline required error only after a Complete attempt, and clear it live as answered.
                const missing =
                  attemptedComplete && q.isRequired && !isAnswered(q);
                return (
                  <div key={q.uid} className="flex flex-col gap-1">
                    <Typography.Text strong type={missing ? "danger" : undefined}>
                      {q.description}
                      {q.isRequired && <span style={{ color: "red" }}> *</span>}
                    </Typography.Text>
                    {q.questionType === "FreeText" && (
                      <Input.TextArea
                        rows={2}
                        status={missing ? "error" : undefined}
                        disabled={readOnly}
                        value={a?.freeText ?? ""}
                        onChange={(e) => onFreeTextChange(q, e.target.value)}
                        onBlur={() => onFreeTextBlur(q)}
                      />
                    )}
                    {q.questionType === "Numeric" && (
                      <Space>
                        <InputNumber
                          status={missing ? "error" : undefined}
                          disabled={readOnly}
                          value={a?.numeric ?? null}
                          min={q.minValue}
                          max={q.maxValue}
                          onChange={(value) =>
                            onNumericChange(q, value as number | null)
                          }
                        />
                        {q.unit && (
                          <Typography.Text type="secondary">{q.unit}</Typography.Text>
                        )}
                      </Space>
                    )}
                    {q.questionType === "SingleChoice" && (
                      <Radio.Group
                        disabled={readOnly}
                        value={a?.selectedChoices?.[0]}
                        onChange={(e) => onChoiceChange(q, [e.target.value])}
                      >
                        <Space direction="vertical">
                          {q.choices.map((c) => (
                            <Radio key={c.uid} value={c.uid}>
                              {c.description}
                            </Radio>
                          ))}
                        </Space>
                      </Radio.Group>
                    )}
                    {q.questionType === "MultipleChoice" && (
                      <Checkbox.Group
                        disabled={readOnly}
                        value={a?.selectedChoices ?? []}
                        onChange={(vals) => onChoiceChange(q, vals as string[])}
                      >
                        <Space direction="vertical">
                          {q.choices.map((c) => (
                            <Checkbox key={c.uid} value={c.uid}>
                              {c.description}
                            </Checkbox>
                          ))}
                        </Space>
                      </Checkbox.Group>
                    )}
                    {missing && (
                      <Typography.Text type="danger" className="text-xs">
                        This question is required.
                      </Typography.Text>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

export default QuestionnaireAnswerModal;
